import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Modal, { ModalHandle as ModalRefHandle } from '../../../components/Modals/Modal';
import Button from '../../../components/Button';
import useToast from '../../../hooks/useToast';
import { api } from '../../../services/api';
import { FaScaleBalanced, FaHourglassHalf, FaBoxArchive, FaTrashCan, FaLandmark, FaCircleInfo } from 'react-icons/fa6';

export interface ModalHandle {
  openModal: (docType: any) => void;
  closeModal: () => void;
}

export interface TtdConfigModalProps {
  reload?: () => void;
}

const HeaderBanner = styled.div`
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  border-radius: 10px;
  padding: 16px 20px;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  .title-group {
    display: flex;
    flex-direction: column;
    gap: 4px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #ffffff;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    span {
      font-size: 12px;
      opacity: 0.9;
    }
  }

  .badge-type {
    background: rgba(255, 255, 255, 0.2);
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    backdrop-filter: blur(4px);
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ fullWidth }) => fullWidth && 'grid-column: 1 / -1;'}

  label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  input, select, textarea {
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    font-size: 13.5px;
    color: #0f172a;
    background: #ffffff;
    box-sizing: border-box;
    transition: all 0.15s ease;

    &:focus {
      outline: none;
      border-color: #0284c7;
      box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15);
    }
  }

  .hint {
    font-size: 11.5px;
    color: #64748b;
  }
`;

const SimulationCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;

  .header-sim {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .pipeline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;

    .step {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 4px;
      flex: 1;
      min-width: 90px;

      .step-icon {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      }

      .step-title {
        font-size: 11px;
        font-weight: 700;
        color: #334155;
        text-transform: uppercase;
      }

      .step-value {
        font-size: 12px;
        color: #64748b;
        font-weight: 600;
      }
    }

    .arrow {
      color: #94a3b8;
      font-size: 16px;
      font-weight: 700;
    }
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
`;

const TtdConfigModal: React.ForwardRefRenderFunction<ModalHandle, TtdConfigModalProps> = ({ reload }, ref) => {
  const modalRef = useRef<ModalRefHandle>(null);
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [docType, setDocType] = useState<any>(null);

  const [formData, setFormData] = useState({
    classificationCode: '000.0',
    currentPhaseYears: 1,
    intermediatePhaseYears: 5,
    finalDestination: 'permanente',
    legalBasis: '',
  });

  React.useImperativeHandle(ref, () => ({
    openModal(data: any) {
      if (data) {
        setDocType(data);
        setFormData({
          classificationCode: data?.classificationCode || '000.0',
          currentPhaseYears: data?.currentPhaseYears ?? 1,
          intermediatePhaseYears: data?.intermediatePhaseYears ?? 5,
          finalDestination: data?.finalDestination || 'permanente',
          legalBasis: data?.legalBasis || 'Lei Federal nº 8.159/1991 e Tabela de Temporalidade Documental (TTD)',
        });
      }
      modalRef.current?.openModal();
    },
    closeModal() {
      modalRef.current?.closeModal();
    },
  }));

  const handleSave = async () => {
    if (!docType?._id) return;

    try {
      setLoading(true);

      const payload = {
        name: docType.name,
        metadata: docType.metadata || [],
        groups: (docType.groups || []).map((g: any) => (typeof g === 'object' ? g._id : g)),
        classificationCode: formData.classificationCode,
        currentPhaseYears: Number(formData.currentPhaseYears) || 0,
        intermediatePhaseYears: Number(formData.intermediatePhaseYears) || 0,
        finalDestination: formData.finalDestination,
        legalBasis: formData.legalBasis,
      };

      await api.put(`/documentTypes/${docType._id}`, payload);

      toast.successToast('Regras da TTD atualizadas com sucesso!');
      modalRef.current?.closeModal();
      if (reload) reload();
    } catch (error) {
      console.error('Erro ao atualizar regras da TTD:', error);
      toast.errorToast('Erro ao atualizar as regras da TTD!');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const currentPhaseEnd = currentYear + (Number(formData.currentPhaseYears) || 0);
  const intermediatePhaseEnd = currentPhaseEnd + (Number(formData.intermediatePhaseYears) || 0);

  return (
    <Modal title='Configuração da Tabela de Temporalidade (TTD)' maxWidth={650} ref={modalRef}>
      <div>
        <HeaderBanner>
          <div className='title-group'>
            <h3>
              <FaScaleBalanced size={18} />
              Regras Arquivísticas da TTD
            </h3>
            <span>Parâmetros de retenção e destinação da CPAD (CONARQ)</span>
          </div>
          {docType?.name && <div className='badge-type'>{docType.name}</div>}
        </HeaderBanner>

        <FormGrid>
          <FormGroup>
            <label>
              Código de Classificação (PCD / CONARQ)
            </label>
            <input
              type='text'
              value={formData.classificationCode}
              onChange={(e) => setFormData({ ...formData, classificationCode: e.target.value })}
              placeholder='Ex: 021.1 ou 033.2'
            />
            <span className='hint'>Identificador no Plano de Classificação de Documentos</span>
          </FormGroup>

          <FormGroup>
            <label>
              Destinação Final
            </label>
            <select
              value={formData.finalDestination}
              onChange={(e) => setFormData({ ...formData, finalDestination: e.target.value })}
            >
              <option value='permanente'>Guarda Permanente (Histórico / Probatório)</option>
              <option value='eliminacao'>Eliminação (Descarte homologado pela CPAD)</option>
            </select>
            <span className='hint'>Destino após cumprimento das fases</span>
          </FormGroup>

          <FormGroup>
            <label>
              <FaHourglassHalf color='#0284c7' size={14} />
              Prazo na Fase Corrente (anos)
            </label>
            <input
              type='number'
              min='0'
              value={formData.currentPhaseYears}
              onChange={(e) => setFormData({ ...formData, currentPhaseYears: Math.max(0, Number(e.target.value)) })}
            />
            <span className='hint'>Tempo no arquivo setorial (uso administrativo frequente)</span>
          </FormGroup>

          <FormGroup>
            <label>
              <FaBoxArchive color='#b45309' size={14} />
              Prazo na Fase Intermediária (anos)
            </label>
            <input
              type='number'
              min='0'
              value={formData.intermediatePhaseYears}
              onChange={(e) => setFormData({ ...formData, intermediatePhaseYears: Math.max(0, Number(e.target.value)) })}
            />
            <span className='hint'>Tempo em custódia central (guarda por precaução/lei)</span>
          </FormGroup>

          <FormGroup fullWidth>
            <label>
              <FaLandmark color='#475569' size={14} />
              Fundamentação Legal / Base Normativa
            </label>
            <textarea
              rows={2}
              value={formData.legalBasis}
              onChange={(e) => setFormData({ ...formData, legalBasis: e.target.value })}
              placeholder='Ex: Lei Federal nº 8.159/1991, Resolução CONARQ nº 14/2001, Código Civil Art. 206...'
            />
            <span className='hint'>Citação de artigos, leis ou regulamentos que amparam os prazos estipulados</span>
          </FormGroup>
        </FormGrid>

        <SimulationCard>
          <div className='header-sim'>
            <FaCircleInfo size={14} />
            Simulador do Ciclo de Vida do Documento
          </div>
          <div className='pipeline'>
            <div className='step'>
              <div className='step-icon' style={{ background: '#e0f2fe', color: '#0369a1' }}>
                📄
              </div>
              <span className='step-title'>Produção</span>
              <span className='step-value'>Ano {currentYear}</span>
            </div>

            <span className='arrow'>→</span>

            <div className='step'>
              <div className='step-icon' style={{ background: '#e0f2fe', color: '#0284c7' }}>
                <FaHourglassHalf />
              </div>
              <span className='step-title'>Corrente</span>
              <span className='step-value'>+{formData.currentPhaseYears} anos ({currentPhaseEnd})</span>
            </div>

            <span className='arrow'>→</span>

            <div className='step'>
              <div className='step-icon' style={{ background: '#fef3c7', color: '#b45309' }}>
                <FaBoxArchive />
              </div>
              <span className='step-title'>Intermediária</span>
              <span className='step-value'>+{formData.intermediatePhaseYears} anos ({intermediatePhaseEnd})</span>
            </div>

            <span className='arrow'>→</span>

            <div className='step'>
              <div
                className='step-icon'
                style={{
                  background: formData.finalDestination === 'eliminacao' ? '#fee2e2' : '#dcfce7',
                  color: formData.finalDestination === 'eliminacao' ? '#b91c1c' : '#15803d',
                }}
              >
                {formData.finalDestination === 'eliminacao' ? <FaTrashCan /> : <FaLandmark />}
              </div>
              <span className='step-title'>Destinação</span>
              <span className='step-value' style={{ textTransform: 'uppercase', fontWeight: 700 }}>
                {formData.finalDestination === 'eliminacao' ? 'Eliminação' : 'Guarda Permanente'}
              </span>
            </div>
          </div>
        </SimulationCard>

        <ActionRow>
          <Button
            type='button'
            label='Cancelar'
            variantType='outline'
            color='secondary'
            onClick={() => modalRef.current?.closeModal()}
          />
          <Button
            type='button'
            label={loading ? 'Salvando...' : 'Salvar Regras da TTD'}
            variantType='block'
            color='primary'
            disabled={loading}
            onClick={handleSave}
          />
        </ActionRow>
      </div>
    </Modal>
  );
};

export default React.forwardRef(TtdConfigModal);
